import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';

import { FortnightlyInvoiceTotal } from './_authenticated._main.invoicing/FortnightlyInvoiceTotal';
import { InvoicesCards } from './_authenticated._main.invoicing/InvoicesCards';
import { InvoicesTable } from './_authenticated._main.invoicing/InvoicesTable';
import { TurnoverByDriverTable } from './_authenticated._main.invoicing/TurnoverByDriverTable';
import { Invoice } from './_authenticated._main.invoicing/types';
import { SimplePagination } from '../components/SimplePagination';
import { prefetchInvoiceResume, useFetchInvoiceResume } from '../hooks/useFetchInvoiceResume';
import { queryKeys } from '../services/datastore/queryKeys';
import { queryClient } from '../services/datastore/react-query';
import { t } from '../services/translation/translation';
import { addDays, formatDate, formatDateForParisTZ, isAfter, isBefore, parseDate, subtractDays } from '../utils/date';

export const FortnightlyInvoicingRoute = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const invoiceResumeKey = searchParams.get('invoiceResumeKey') ?? undefined;
  const [selectedDate, setSelectedDate] = useState<string | undefined>();

  const { data, isLoading, isFetching, isPreviousData } = useFetchInvoiceResume({
    date: selectedDate,
    invoiceResumeKey: selectedDate ? undefined : invoiceResumeKey,
  });
  const invoices = useMemo<Invoice[]>(() => {
    if (!data) return [];
    return [
      ...data.invoiceIn.map(i => ({ ...i, type: 'in' as const })),
      ...data.invoiceOut.map(i => ({ ...i, type: 'out' as const })),
    ];
  }, [data]);

  const disabledNext =
    !data?.endDate ||
    isBefore(Date.now(), data?.endDate) ||
    isAfter(addDays(data.endDate, 1), parseDate('2024-12-31', 'YYYY-MM-DD'));
  const previousDate = data ? formatDateForParisTZ(subtractDays(data.startDate, 1), 'YYYY-MM-DD', false) : '';
  const nextDate = data ? formatDateForParisTZ(addDays(data.endDate, 1), 'YYYY-MM-DD', false) : '';

  const handlePrevious = () => {
    if (!previousDate) return;
    setSelectedDate(previousDate);
  };

  const handleNext = () => {
    if (!nextDate) return;
    setSelectedDate(nextDate);
  };

  useEffect(() => {
    if (data?.id) {
      setSearchParams(params => {
        params.set('invoiceResumeKey', data.id);
        return params;
      });
    }
  }, [data?.id, setSearchParams]);

  useEffect(() => {
    if (previousDate) prefetchInvoiceResume({ date: previousDate });
    if (nextDate) prefetchInvoiceResume({ date: nextDate });
  }, [previousDate, nextDate]);

  useEffect(() => {
    if (data) {
      queryClient.setQueryData(queryKeys.user.fetchInvoiceResume({ invoiceResumeKey: data.id }), data);
    }
  }, [data]);

  return (
    <div>
      <Helmet>
        <title>{t('invoicing-route.title-fortnight')} - Allocab</title>
      </Helmet>

      <h3 className="text-3xl font-bold dark:text-white mb-6">{t('invoicing-route.title-fortnight')}</h3>

      <div className="space-y-4">
        <SimplePagination
          onNext={handleNext}
          onPrevious={handlePrevious}
          isLoading={isLoading}
          isNextDisabled={disabledNext}
          isPreviousDisabled={!data}
        >
          {data
            ? t('invoicing-route.payment-interval', {
                startDate: formatDateForParisTZ(data.startDate, t('date.date-longer'), false),
                endDate: formatDateForParisTZ(data.endDate, t('date.date-longer'), false),
              })
            : '--'}
        </SimplePagination>
        <FortnightlyInvoiceTotal data={data} isFetching={isPreviousData && isFetching} />
        {data?.driversDetails ? (
          <TurnoverByDriverTable
            data={data.driversDetails}
            isLoading={isLoading}
            isFetching={isPreviousData && isFetching}
          />
        ) : null}
        <div className="hidden xl:block">
          <InvoicesTable invoices={invoices} isLoading={isLoading} isFetching={isPreviousData && isFetching} />
        </div>
        <div className="block xl:hidden">
          <InvoicesCards
            invoices={invoices}
            isLoading={isLoading}
            isFetching={isPreviousData && isFetching}
            title={t('common.invoices')}
          />
        </div>
      </div>
    </div>
  );
};
